import { useEffect } from 'react'

import { apiZodios } from '~api/apiZodios'
import { API_URL } from '~config'
import { useGenerateResponseStreamMutation } from '~redux/slices/apiSlice'
import { QueryAnalysis, ResponseDisplayLink } from '~types'

export const preTextStreamResponseSteps = [
  'initialization',
  'query_analysis',
  'retrieval',
  'gis_lookup',
  'generation',
] as const

export type PreTextStreamResponseStep = (typeof preTextStreamResponseSteps)[number]

export type StreamResponseStep =
  | PreTextStreamResponseStep
  | 'partial_response'
  | 'complete_response'
  | 'error'

interface StreamResponse {
  step: StreamResponseStep
  user_id?: string
  ai_interaction_id?: string
  query_analysis?: QueryAnalysis
  response_id?: string
  response_text?: string
  sources?: ResponseDisplayLink[]
}

export const useStreamAIResponse = (
  onMessage: (response: StreamResponse, isFirstResponse: boolean) => void,
  onError: (errorMessage: string) => void,
): {
  isGeneratingResponseStream: boolean
  streamAIResponse: (request: Parameters<typeof apiZodios.ai_generateResponseStream>[0]) => void
} => {
  const [generateResponseStream, { isLoading: isGeneratingResponseStream, isError, data }] =
    useGenerateResponseStreamMutation()

  useEffect(() => {
    if (isError) {
      onError('Error generating response. Please try entering your message again.')
    }
  }, [isError])

  useEffect(() => {
    if (!data?.response_stream_id) {
      return
    }

    // Open connection to response stream
    const eventSource = new EventSource(
      `${API_URL}/ai/read-response-stream/${data.response_stream_id}/`,
    )
    let receivedFirstResponseTextChunk = false
    eventSource.onmessage = (event): void => {
      try {
        const response = JSON.parse(event.data) as StreamResponse
        if (response.step === 'error') {
          onError('Error reading response. Please try entering your message again.')
          eventSource.close()
          return
        }

        onMessage(response, receivedFirstResponseTextChunk)

        // Set flag to true after the first response with response text is received
        if (response.step === 'partial_response') {
          receivedFirstResponseTextChunk = true
        } else if (response.step === 'complete_response') {
          eventSource.close()
        }
      } catch {
        onError('Error parsing response. Please try entering your message again.')
        eventSource.close()
      }
    }

    // Close connection on unmount
    return (): void => eventSource.close()
  }, [data?.response_stream_id])

  return {
    isGeneratingResponseStream,
    streamAIResponse: (request) =>
      generateResponseStream({ ...request, ai_interaction_id: data?.ai_interaction_id }),
  }
}
